import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const CostCodes = () => {
  const [costCodes, setCostCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCostCode, setEditingCostCode] = useState(null);
  const [errors, setErrors] = useState({});
  const [filteredCostCodes, setFilteredCostCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [costCodesPerPage] = useState(20);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [divisionDropdownOpen, setDivisionDropdownOpen] = useState(false);
  const [formStatusDropdownOpen, setFormStatusDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    division: '',
    status: 'active'
  });
  
  // Integration state
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [syncingCostCodes, setSyncingCostCodes] = useState(false);

  useEffect(() => {
    fetchCostCodes();
  }, []);

  // Add click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setStatusDropdownOpen(false);
        setDivisionDropdownOpen(false);
        setFormStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter and paginate cost codes whenever cost codes or filters change
  useEffect(() => {
    filterAndPaginateCostCodes();
  }, [costCodes, searchTerm, statusFilter, divisionFilter, currentPage]);

  const filterAndPaginateCostCodes = () => {
    let filtered = costCodes;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(costCode =>
        (costCode.code && costCode.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (costCode.description && costCode.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (costCode.division && costCode.division.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(costCode => costCode.status === statusFilter);
    }

    // Apply division filter
    if (divisionFilter !== 'all') {
      filtered = filtered.filter(costCode => costCode.division === divisionFilter);
    }

    setFilteredCostCodes(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    try {
      const baseURL = 'http://localhost:5001';
      if (editingCostCode) {
        await axios.put(`${baseURL}/api/costcodes/${editingCostCode.vuid}`, formData);
      } else {
        await axios.post(`${baseURL}/api/costcodes`, formData);
      }
      fetchCostCodes();
      setShowForm(false);
      setEditingCostCode(null);
      setFormData({
        code: '',
        description: '',
        division: '',
        status: 'active'
      });
    } catch (error) {
      console.error('Error saving cost code:', error);
      setErrors(error.response?.data || { error: 'An error occurred' });
    }
  };

  const handleEdit = (costCode) => {
    setEditingCostCode(costCode);
    setFormData({
      code: costCode.code,
      description: costCode.description,
      division: costCode.division,
      status: costCode.status
    });
    setShowForm(true);
  };

  const handleDelete = async (costCode) => {
    if (window.confirm(`Are you sure you want to delete the cost code "${costCode.code}"?`)) {
      try {
        const baseURL = 'http://localhost:5001';
        await axios.delete(`${baseURL}/api/costcodes/${costCode.vuid}`);
        fetchCostCodes();
      } catch (error) {
        console.error('Error deleting cost code:', error);
        alert('Error deleting cost code');
      }
    }
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'search':
        setSearchTerm(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      case 'division':
        setDivisionFilter(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const getUniqueDivisions = () => {
    const divisions = [...new Set(costCodes.map(code => code.division))];
    return divisions.sort();
  };

  const getCurrentCostCodes = () => {
    const startIndex = (currentPage - 1) * costCodesPerPage;
    const endIndex = startIndex + costCodesPerPage;
    return filteredCostCodes.slice(startIndex, endIndex);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const fetchCostCodes = async () => {
    try {
      const baseURL = 'http://localhost:5001';
      const response = await axios.get(`${baseURL}/api/costcodes`);
      setCostCodes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cost codes:', error);
      setLoading(false);
    }
  };

  // Integration functions
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      
      // Filter integrations to only show those that have 'cost_codes' enabled
      const filteredIntegrations = response.data.filter(integration => 
        integration.enabled_objects && integration.enabled_objects.cost_codes === true
      );
      
      setIntegrations(filteredIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleIntegrationModal = () => {
    setSelectedIntegration(null);
    setShowIntegrationModal(true);
    fetchIntegrations();
  };

  const handleSendCostCodesToIntegration = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    if (selectedIntegration.integration_type !== 'procore') {
      alert('Cost code syncing is currently only supported for Procore integrations');
      return;
    }

    const activeCostCodesCount = costCodes.filter(cc => cc.status === 'active').length;
    
    if (activeCostCodesCount === 0) {
      alert('No active cost codes to sync');
      return;
    }

    if (!window.confirm(`This will sync ${activeCostCodesCount} active cost codes to ${selectedIntegration.integration_name}. Continue?`)) {
      return;
    }

    try {
      setSyncingCostCodes(true);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/sync-cost-codes`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        let message = response.data.message || `Successfully synced cost codes to ${selectedIntegration.integration_name}`;
        
        // Build detailed message
        const parts = [];
        if (response.data.successful > 0) {
          if (response.data.newly_created > 0 && response.data.already_exists > 0) {
            parts.push(`${response.data.newly_created} newly created, ${response.data.already_exists} already existed`);
          } else if (response.data.newly_created > 0) {
            parts.push(`${response.data.newly_created} synced`);
          } else if (response.data.already_exists > 0) {
            parts.push(`${response.data.already_exists} already exist`);
          } else {
            parts.push(`${response.data.successful} synced`);
          }
        }
        if (response.data.errors > 0) {
          parts.push(`${response.data.errors} errors`);
        }
        
        if (parts.length > 0) {
          message = `Cost codes sync: ${parts.join(', ')}`;
        }
        
        // Add details if there are already exists or errors
        if (response.data.already_exists > 0 && response.data.already_exists_details && response.data.already_exists_details.length > 0) {
          message += `\n\nAlready exist (showing first ${Math.min(5, response.data.already_exists_details.length)}):\n`;
          message += response.data.already_exists_details.slice(0, 5).join('\n');
          if (response.data.already_exists_details.length > 5) {
            message += `\n... and ${response.data.already_exists_details.length - 5} more`;
          }
        }
        
        if (response.data.errors > 0 && response.data.error_details && response.data.error_details.length > 0) {
          message += `\n\nErrors:\n`;
          message += response.data.error_details.slice(0, 5).join('\n');
          if (response.data.error_details.length > 5) {
            message += `\n... and ${response.data.error_details.length - 5} more`;
          }
        }
        
        alert(message);
        
        // Close the modal
        setShowIntegrationModal(false);
        setSelectedIntegration(null);
      } else {
        alert(`Error: ${response.data.error || 'Failed to sync cost codes'}`);
      }
    } catch (error) {
      console.error('Error syncing cost codes to integration:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to sync cost codes to integration';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSyncingCostCodes(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">Loading cost codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cost Codes</h1>
          <p className="text-xl text-gray-600">Manage construction cost codes and classifications</p>
        </div>

        {/* Add Cost Code Button and Send to Integration Button */}
        <div className="mb-8 flex justify-center gap-4">
          <button
            onClick={() => {
              setEditingCostCode(null);
              setFormData({
                code: '',
                description: '',
                division: '',
                status: 'active'
              });
              setShowForm(true);
            }}
            className="btn-claret"
            data-tooltip="Add Cost Code"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Cost Code</span>
          </button>
          <button
            onClick={handleIntegrationModal}
            className={`btn-claret ${
              costCodes.filter(cc => cc.status === 'active').length === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={costCodes.filter(cc => cc.status === 'active').length === 0}
            data-tooltip="Send to Integration"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span>Send to Integration</span>
          </button>
        </div>

        {/* Create/Edit Cost Code Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {editingCostCode ? 'Edit Cost Code' : 'Add New Cost Code'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-medium">{errors.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="code" className="block text-lg font-semibold text-gray-900 mb-2">
                    Code *
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    placeholder="e.g., 00 01 01"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="division" className="block text-lg font-semibold text-gray-900 mb-2">
                    Division *
                  </label>
                  <input
                    type="text"
                    id="division"
                    name="division"
                    value={formData.division}
                    onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    placeholder="e.g., 00"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-lg font-semibold text-gray-900 mb-2">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all resize-none"
                    placeholder="Enter cost code description"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="status" className="block text-lg font-semibold text-gray-900 mb-2">
                    Status
                  </label>
                  <div className="relative dropdown-container">
                    <button
                      type="button"
                      onClick={() => setFormStatusDropdownOpen(!formStatusDropdownOpen)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all text-left bg-white"
                    >
                      {formData.status === 'active' ? 'Active' : 'Inactive'}
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {formStatusDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, status: 'active' });
                              setFormStatusDropdownOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                          >
                            Active
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, status: 'inactive' });
                              setFormStatusDropdownOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                          >
                            Inactive
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  type="submit"
                  className="btn-claret"
                  data-tooltip={editingCostCode ? 'Update Cost Code' : 'Create Cost Code'}
                  >
                  <span>{editingCostCode ? 'Update Cost Code' : 'Create Cost Code'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingCostCode(null);
                    setErrors({});
                  }}
                  className="btn-claret"
                  data-tooltip="Cancel"
                >
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search cost codes..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                value={searchTerm}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
              >
                Status: {statusFilter === 'all' ? 'All' : statusFilter}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleFilterChange('status', 'all');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All
                    </button>
                    <button
                      onClick={() => {
                        handleFilterChange('status', 'active');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Active
                    </button>
                    <button
                      onClick={() => {
                        handleFilterChange('status', 'inactive');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Inactive
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Division Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setDivisionDropdownOpen(!divisionDropdownOpen)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
              >
                Division: {divisionFilter === 'all' ? 'All' : divisionFilter}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {divisionDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300 max-h-96 overflow-y-auto">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleFilterChange('division', 'all');
                        setDivisionDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All
                    </button>
                    {getUniqueDivisions().map(division => (
                      <button
                        key={division}
                        onClick={() => {
                          handleFilterChange('division', division);
                          setDivisionDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        {division}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cost Codes Table */}
        {filteredCostCodes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No cost codes found</h3>
            <p className="text-gray-600">Get started by creating your first cost code.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Division
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getCurrentCostCodes().map((costCode) => (
                    <tr key={costCode.vuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {costCode.code}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {costCode.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {costCode.division || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          costCode.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {costCode.status.charAt(0).toUpperCase() + costCode.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(costCode)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Edit cost code"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(costCode)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Delete cost code"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredCostCodes.length > costCodesPerPage && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.ceil(filteredCostCodes.length / costCodesPerPage) }, (_, i) => i + 1).map((number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === number
                      ? 'bg-vermillion-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === Math.ceil(filteredCostCodes.length / costCodesPerPage)}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}

        {/* Send to Integration Modal */}
        {showIntegrationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900">Send Cost Codes to Integration</h2>
                <button 
                  onClick={() => {
                    setShowIntegrationModal(false);
                    setSelectedIntegration(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Cost Codes Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cost Codes Summary</h3>
                <div className="text-sm text-gray-700">
                  <p><span className="font-medium">Total Cost Codes:</span> {costCodes.length}</p>
                  <p><span className="font-medium">Active Cost Codes:</span> {costCodes.filter(cc => cc.status === 'active').length}</p>
                  <p className="mt-2 text-gray-600">Only active cost codes will be synced to the integration.</p>
                </div>
              </div>
              
              {/* Integration Selection */}
              <div className="mb-6">
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  Select Integration
                </label>
                {integrations.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">
                      No integrations found with cost codes enabled. Please configure an integration in the Integrations page.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {integrations.map((integration) => (
                      <div
                        key={integration.vuid}
                        onClick={() => setSelectedIntegration(integration)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedIntegration?.vuid === integration.vuid
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                            <p className="text-sm text-gray-600 capitalize">{integration.integration_type?.replace('_', ' ')}</p>
                          </div>
                          {selectedIntegration?.vuid === integration.vuid && (
                            <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowIntegrationModal(false);
                    setSelectedIntegration(null);
                  }}
                  className="btn-claret"
                  disabled={syncingCostCodes}
                  data-tooltip="Cancel"
                >
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSendCostCodesToIntegration}
                  disabled={!selectedIntegration || syncingCostCodes || integrations.length === 0}
                  className={`btn-claret ${
                    !selectedIntegration || syncingCostCodes || integrations.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  data-tooltip="Sync Cost Codes"
                >
                  {syncingCostCodes ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Sync Cost Codes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostCodes;